import React, { ReactElement, ReactNode } from 'react';
import {
    Container,
    StyledLabel,
    Wrapper,
    ErrorMessage,
    FieldWrapper
} from './HBField.styles';
import type {
    HBFieldPlacement,
    TFieldSize,
    TFontWeight
} from './HBField.types';
import { stopEventPropagation } from '../../utils/shared.utils';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import { TControlSize } from '../../tokens/types';

interface BaseProps {
    /** HTML `id` forwarded to the label's `htmlFor`; clicking the label focuses the child input. */
    id?: string;
    /** Label text or element displayed next to the child input control. */
    label?: ReactNode;
    /** The input control to wrap. */
    children: ReactElement;
    /** Renders label and input side-by-side in a horizontal layout. */
    inline?: boolean;
    /** Position of the label relative to the child input control. Defaults to `'top'`. */
    placement?: HBFieldPlacement;
    /** Appends an asterisk (*) to the label text to indicate a required field. */
    isRequired?: boolean;
    /** Applies disabled styling to the label. Pass `disabled` to the child input separately to prevent interaction. */
    disabled?: boolean;
    /** Font weight of the label text. Defaults to `'normal'`. */
    fontWeight?: TFontWeight;
    /** Right margin of the outer container. Defaults to `'8px'`. */
    marginRight?: string;
    /** Bottom margin of the outer container. Defaults to `'8px'`. */
    marginBottom?: string;
    /** Left margin of the outer container. Defaults to `'8px'`. */
    marginLeft?: string;
    /** Top margin of the outer container. Defaults to `'0px'`. */
    marginTop?: string;
    /** Additional CSS class applied to the outer container. */
    className?: string;
    /** Width of the outer container. */
    width?: string;
    /** Color override for the label text. */
    color?: string;
    /** Adjusts label styling to accommodate an icon next to the label text. */
    withIcon?: boolean;
    /** Content rendered before the label, such as an info icon or badge. */
    preLabelContent?: ReactNode;
    /** Content rendered after the label, such as a help button or action. */
    postLabelContent?: ReactNode;
    /** Fully qualified test ID for the field container element. */
    testId?: string;
    /** Suffix used to construct the `data-testid`; falls back to `id` when omitted. */
    testIdName?: string;
    /** @deprecated Enables DS Next styling mode — unlocks inline error message display and the DS Next size scale. Will be removed post-migration. */
    isDSNext?: boolean;
    /** Marks the field as invalid. Error text is only shown when `isDSNext`, `error`, and `errorMessage` are all set and the field is not disabled. */
    error?: boolean;
    /** Error text displayed below the field; only visible when isDSNext and error are true and the field is not disabled. */
    errorMessage?: string | null;
    /** Renders the label as a group label (e.g., for fieldset legends) instead of a standard form label. */
    isGroupField?: boolean;
}

interface DSNextProps extends BaseProps {
    isDSNext: true;
    /** Controls label text size and error message spacing using the DS Next size scale. */
    size?: TControlSize;
}

interface LegacyProps extends BaseProps {
    isDSNext?: false;
    /** Controls label text size using the legacy size scale (xxs through xl). */
    size?: TFieldSize;
}

export type Props = DSNextProps | LegacyProps;

const HBField = ({
    id,
    testIdName,
    testId,
    label,
    children,
    inline = false,
    placement = 'top',
    isRequired = false,
    size = 'lg',
    disabled = false,
    fontWeight = 'normal',
    marginRight = '8px',
    marginBottom = '8px',
    marginLeft = '8px',
    marginTop = '0px',
    className,
    width,
    color,
    withIcon = false,
    preLabelContent,
    postLabelContent,
    isDSNext = false,
    error = false,
    errorMessage = null,
    isGroupField = false
}: Props & MustIncludeOneOfBasicTestIdWithIdProps) => {
    const isErrorVisible = isDSNext && error && errorMessage && !disabled;
    const spacing = {
        marginTop,
        marginRight,
        marginBottom,
        marginLeft
    };

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id,
        suffix: 'field',
        componentName: 'HBField'
    });

    const labelTestId = finalTestId?.replace(/--field$/, '--label');

    const contentWrapper = () => {
        const labelComponent = label && (
            <StyledLabel
                id={id}
                testId={labelTestId}
                testIdName={testIdName}
                isGroupLabel={isGroupField}
                text={label}
                withIcon={withIcon}
                size={size}
                inline={inline}
                placement={placement}
                disabled={disabled}
                fontWeight={fontWeight}
                isRequired={isRequired}
                spacing={spacing}
                width={width}
                color={color}
                isDSNext={isDSNext}
            />
        );

        if (preLabelContent || postLabelContent) {
            return (
                <Wrapper>
                    {preLabelContent}
                    {labelComponent}
                    {postLabelContent}
                </Wrapper>
            );
        }

        return labelComponent;
    };

    return (
        <FieldWrapper>
            <Container
                inline={inline}
                placement={placement}
                className={className}
                onPaste={stopEventPropagation}
                data-testid={finalTestId}
                isDSNext={isDSNext}
            >
                {contentWrapper()}
                {children}
            </Container>

            {isErrorVisible && (
                <ErrorMessage $spacingTop={size as TControlSize} type="body12">
                    {errorMessage}
                </ErrorMessage>
            )}
        </FieldWrapper>
    );
};

export default HBField;
